// AuthContext with proper TypeScript
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'
import { Session, User } from '@supabase/supabase-js'
import { Capacitor } from '@capacitor/core'
import { useLocation, useNavigate } from 'react-router-dom'

// TypeScript declaration for Google Ads conversion tracking
declare global {
  interface Window {
    trackPurchaseConversion?: (transactionId?: string) => void;
  }
}

// Keep the original TS check disabled until we can properly fix the type issues
interface AuthContextType {
  user: User | null
  session: Session | null
  hasSubscription: boolean
  isTrial: boolean
  trialEndDate: string | null
  trialDaysLeft: number | null
  loading: boolean
  logout: () => Promise<void>
  clearSession: () => void
  updateSubscription: (value: boolean) => void
  setUser: (user: User | null) => void
  setSession: (session: Session | null) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  
  // Initialize subscription status from localStorage for persistence
  const [hasSubscription, setHasSubscription] = useState<boolean>(() => {
    const stored = localStorage.getItem('hasSubscription')
    return stored === 'true'
  })
  
  // Add trial states
  const [isTrial, setIsTrial] = useState<boolean>(() => {
    const stored = localStorage.getItem('isTrial')
    return stored === 'true'
  })
  const [trialEndDate, setTrialEndDate] = useState<string | null>(() => {
    return localStorage.getItem('trialEndDate')
  })
  const [trialDaysLeft, setTrialDaysLeft] = useState<number | null>(null)
  
  const [loading, setLoading] = useState(true)
  const [subscriptionChecking, setSubscriptionChecking] = useState(false)
  const [redirectInProgress, setRedirectInProgress] = useState(false)

  // Ref to track and cleanup polling
  const pollingCleanupRef = useRef<(() => void) | null>(null)
  
  // Ref to prevent duplicate subscription fetches
  const fetchingSubscriptionRef = useRef(false)

  // Function to update subscription status and persist to localStorage
  const updateSubscription = (value: boolean) => {
    console.log('📝 Updating subscription status to:', value)
    setHasSubscription(value)
    localStorage.setItem('hasSubscription', value ? 'true' : 'false')
  }
  
  // Function to update trial status
  const updateTrialStatus = (isActive: boolean, endDate: string | null) => {
    console.log('📝 Updating trial status:', { isActive, endDate })
    setIsTrial(isActive)
    setTrialEndDate(endDate)
    localStorage.setItem('isTrial', isActive ? 'true' : 'false')
    if (endDate) {
      localStorage.setItem('trialEndDate', endDate)
    } else {
      localStorage.removeItem('trialEndDate')
    }
  }

  // ADDED: Function to update trial status in database
  const updateTrialInDatabase = async (
    email: string, 
    isActive: boolean, 
    endDate: string | null = null
  ) => {
    // Guard against inconsistent state: active trial must have an end date
    if (isActive && !endDate) {
      console.error(
        'updateTrialInDatabase called with isActive=true but endDate missing – aborting'
      );
      return;
    }
    
    try {
      console.log(`📝 Updating trial in database for ${email}: ${isActive}`)
      const { data, error } = await supabase
        .from('profiles')
        .update({ 
          is_trial: isActive,
          trial_end_date: isActive ? endDate : null 
        })
        // Use exact match with lowercase email
        .eq('email', email.toLowerCase())
        .select()
      
      if (error) {
        console.error('❌ Failed to update trial in database:', error)
      } else if (!data || data.length === 0) {
        console.error('❌ No profile found to update for email:', email)
      } else {
        console.log('✅ Trial status updated in database')
      }
    } catch (err) {
      console.error('❌ Database update error:', err)
    }
  }

  // Helper function to track conversion when subscription is newly activated
  const trackConversionIfNew = (email: string, isNewSubscription: boolean = false) => {
    if (isNewSubscription && typeof window !== 'undefined' && window.trackPurchaseConversion) {
      console.log('🎉 New subscription detected - tracking Google Ads conversion');
      window.trackPurchaseConversion(email);
    }
  }

  // IMPORTANT: Always keep loading true when checking subscription
  useEffect(() => {
    if (subscriptionChecking) {
      setLoading(true)
    }
  }, [subscriptionChecking])

  // ENHANCED: Calculate days left in trial with real-time expiration handling
  useEffect(() => {
    let isMounted = true;
    
    if (trialEndDate) {
      const checkTrialExpiration = async () => {
        const endDate = new Date(trialEndDate)
        
        // Guard against malformed trialEndDate values
        if (isNaN(endDate.getTime())) {
          console.warn('⚠️ Invalid trialEndDate found – clearing it');
          updateTrialStatus(false, null);
          return;
        }
        
        const now = new Date()
        
        if (!isMounted) return;
        
        if (endDate > now) {
          const diffTime = endDate.getTime() - now.getTime()
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
          setTrialDaysLeft(diffDays)
        } else {
          if (!isMounted) return;
          
          setTrialDaysLeft(0)
          
          // ENHANCED: Trial expired - update both local and database
          if (isTrial) {
            console.log('⏱️ Trial expired - updating local and database state')
            updateTrialStatus(false, null)
            
            // Update database if user is logged in - pass explicit endDate
            if (user?.email) {
              await updateTrialInDatabase(user.email, false, null)
            }
          }
        }
      }
      
      checkTrialExpiration()
      
      // ADDED: Set up interval to check every minute for trial expiration
      const interval = setInterval(() => {
        // Run in plain function to get latest values
        checkTrialExpiration()
      }, 60000) // Check every minute
      
      return () => {
        clearInterval(interval)
        isMounted = false;
      }
    } else {
      setTrialDaysLeft(null)
    }
  }, [trialEndDate, isTrial, user?.email])

  // SECURITY: Debug mode only in development environment and native platforms
  useEffect(() => {
    const isDevelopment = process.env.NODE_ENV === 'development' || 
                         window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1'
    
    if (isDevelopment && Capacitor.isNativePlatform() && localStorage.getItem('debug_mode') === 'true') {
      try {
        const debugUser = JSON.parse(localStorage.getItem('debug_user') || '{}')
        // SECURITY: Only allow debug mode in development with valid debug user structure
        if (debugUser.id && debugUser.email) {
          setUser(debugUser)
          updateSubscription(true)
          setLoading(false)
          console.log('🐞 Debug mode active (development only) - bypassing authentication')
          return // Skip normal auth flow
        } else {
          console.warn('🐞 Invalid debug user data, continuing with normal auth flow')
        }
      } catch (error) {
        console.error('Debug mode error:', error)
        // Continue with normal flow if debug mode fails
      }
    }
    
    // Normal authentication flow continues below...
    
    const initializeAuth = async () => {
      try {
        console.log('🔍 Initializing auth...')
        
        // ENHANCED: Check stored subscription first with proper trial expiration
        const storedSubscription = localStorage.getItem('hasSubscription') === 'true'
        const storedTrial = localStorage.getItem('isTrial') === 'true'
        const storedTrialEnd = localStorage.getItem('trialEndDate')
        
        if (storedSubscription) {
          console.log('💾 Found subscription in localStorage')
          updateSubscription(true)
        }
        
        // FIXED: Check trial expiration immediately on startup
        if (storedTrial && storedTrialEnd) {
          const trialEnd = new Date(storedTrialEnd)
          const now = new Date()
          
          if (trialEnd > now) {
            console.log('💾 Found valid trial in localStorage')
            setIsTrial(true)
          } else {
            console.log('⏱️ Trial expired - clearing localStorage and will update database')
            // Clear expired trial from localStorage
            updateTrialStatus(false, null)
            // Note: Database will be updated when session is available
          }
        }
        
        const { data, error } = await supabase.auth.getSession()
        
        if (error) {
          throw error
        }
        
        if (data?.session) {
          setSession(data.session)
          setUser(data.session.user)
          
          // Check subscription status
          if (data.session.user.email) {
            // IMPORTANT: Keep loading true during subscription check
            setSubscriptionChecking(true)
            await fetchSubscription(data.session.user.email)
          } else {
            updateSubscription(false)
            setSubscriptionChecking(false)
            setLoading(false)
          }
        } else {
          updateSubscription(false)
          updateTrialStatus(false, null)
          setSubscriptionChecking(false)
          setLoading(false)
        }
      } catch (err) {
        console.error('❌ Auth initialization error:', err)
        updateSubscription(false)
        updateTrialStatus(false, null)
        setSubscriptionChecking(false)
        setLoading(false)
      }
    }
    
    initializeAuth()
    
    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('🔄 Auth state changed:', event)
        
        // Process meaningful auth changes
        if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
          if (newSession) {
            setSession(newSession)
            setUser(newSession.user)
            
            // Fetch subscription on initial session or sign in
            if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN') && newSession.user?.email) {
              console.log(`🔍 Fetching subscription for ${event} event with email:`, newSession.user.email)
              setSubscriptionChecking(true)
              await fetchSubscription(newSession.user.email)
            } else if (event === 'TOKEN_REFRESHED' && newSession.user?.email) {
              // Verify subscription on token refresh to prevent stale data
              // Rate-limited to prevent excessive API calls
              const lastVerified = localStorage.getItem('lastSubscriptionVerified')
              const shouldVerify = !lastVerified || (Date.now() - parseInt(lastVerified)) > 300000 // 5 minutes
              if (shouldVerify) {
                console.log('🔄 Token refreshed - verifying subscription status')
                setSubscriptionChecking(true)
                await fetchSubscription(newSession.user.email)
              }
            } else if (event !== 'INITIAL_SESSION') {
              setSubscriptionChecking(false)
              setLoading(false)
            }
          } else {
            setSession(null)
            setUser(null)
            updateSubscription(false)
            updateTrialStatus(false, null)
            setSubscriptionChecking(false)
            setLoading(false)
          }
        }
      }
    )
    
    return () => {
      authListener?.subscription.unsubscribe()
      // Cleanup any active polling on unmount
      if (pollingCleanupRef.current) {
        pollingCleanupRef.current()
        pollingCleanupRef.current = null
      }
    }
  }, [])

  // Handle initial redirects ONLY ONCE after auth is completed
  useEffect(() => {
    // Only perform redirection if we're fully loaded and not already redirecting
    if (!loading && user && !redirectInProgress) {
      const currentPath = location.pathname;
      
      // FIXED: Special exception for pricing page to allow upgrade flow
      if (currentPath === '/pricing') {
        console.log('🔍 User is on pricing page, allowing access for upgrade')
        return; // Don't redirect away from pricing
      }
      
      // FIXED: Special exception for affiliate portal
      if (currentPath === '/affiliate-portal') {
        console.log('🔍 User is on affiliate portal, allowing access for affiliate check')
        return; // Don't redirect away from affiliate portal
      }
      
      // Handle the case where user is authenticated but not on an app route
      if ((hasSubscription || isTrial) && !currentPath.startsWith('/app/')) {
        setRedirectInProgress(true);
        console.log('🔄 Initial redirect to /app/calendar from:', currentPath);
        console.log('🔄 Auth status - subscription:', hasSubscription, 'trial:', isTrial);
        navigate('/app/calendar');
        // Reset redirect flag after navigation
        setTimeout(() => setRedirectInProgress(false), 100);
      }
      
      // Handle the case where user has no access but is on a protected route
      if (!hasSubscription && !isTrial && currentPath.startsWith('/app/')) {
        setRedirectInProgress(true);
        console.log('🔄 Redirect to pricing - no subscription or trial');
        navigate('/pricing');
        // Reset redirect flag after navigation
        setTimeout(() => setRedirectInProgress(false), 100);
      }
    }
  }, [loading, user, hasSubscription, isTrial, location.pathname, navigate, redirectInProgress]);

  // ENHANCED: startPolling with proper cleanup to prevent memory leaks
  function startPolling(email: string) {
    let attempts = 0
    const maxAttempts = 10
    const pollingInterval = 2000 // 2 seconds
    let disposed = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    
    const poll = async () => {
      if (disposed) return
      
      console.log(`🔄 Polling for subscription (attempt ${attempts + 1}/${maxAttempts})`)
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('is_subscribed, is_trial, trial_end_date')
          .eq('email', email.toLowerCase())
          .single()
          
        if (disposed) return // Check again after async operation
          
        if (error) {
          console.error('❌ Polling error:', error)
        } else if (data) {
          if (data.is_subscribed) {
            console.log('✅ Subscription found on polling!')
            
            // 🎯 This is definitely a NEW subscription since we're polling for it
            const isNewSubscription = true;
            
            updateSubscription(true)
            
            // Track conversion for new subscription found during polling
            trackConversionIfNew(email, isNewSubscription);
            
            setSubscriptionChecking(false)
            setLoading(false)
            
            // Mark disposed and clear pending timer
            disposed = true
            if (timeoutId) clearTimeout(timeoutId)
            return // Stop polling
          }
          
          // ENHANCED: Check for active trial during polling with expiration handling
          if (data.is_trial && data.trial_end_date) {
            const trialEnd = new Date(data.trial_end_date)
            const now = new Date()
            
            if (trialEnd > now) {
              // Active trial
              console.log('🎁 Active trial found on polling, valid until:', trialEnd)
              updateTrialStatus(true, data.trial_end_date)
              setSubscriptionChecking(false)
              setLoading(false)
              
              // Mark disposed and clear pending timer
              disposed = true
              if (timeoutId) clearTimeout(timeoutId)
              return // Stop polling
            } else {
              // Trial expired during polling - update database with explicit null endDate
              console.log('⏱️ Expired trial found during polling - updating database')
              await updateTrialInDatabase(email, false, null)
            }
          }
        }
      } catch (err) {
        console.error('❌ Polling error:', err)
      }
      
      attempts++
      
      if (attempts < maxAttempts && !disposed) {
        // Schedule next poll
        timeoutId = setTimeout(poll, pollingInterval)
      } else {
        console.log('⚠️ Max polling attempts reached or polling disposed')
        if (!disposed) {
          setSubscriptionChecking(false)
          setLoading(false)
        }
      }
    }
    
    // Start first poll after a short delay
    timeoutId = setTimeout(poll, pollingInterval)
    
    // Return cleanup function
    return () => {
      disposed = true
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }
  }

  // ENHANCED: fetchSubscription with proper trial expiration handling
  const fetchSubscription = async (email: string | undefined) => {
    if (!email) {
      console.error('❌ No email provided for subscription check')
      updateSubscription(false)
      updateTrialStatus(false, null)
      setSubscriptionChecking(false)
      setLoading(false)
      return
    }
    
    // Prevent duplicate subscription fetches
    if (fetchingSubscriptionRef.current) {
      console.log('🔄 Subscription fetch already in progress, skipping duplicate')
      return
    }
    
    fetchingSubscriptionRef.current = true
    
    try {
      console.log('🔍 Fetching subscription and trial status for email:', email)
      
      // SECURITY: Always verify subscription status from database
      // localStorage is only used for performance optimization, not security decisions
      const storedSubscription = localStorage.getItem('hasSubscription')
      const storedTrial = localStorage.getItem('isTrial')
      const storedTrialEnd = localStorage.getItem('trialEndDate')
      const lastVerified = localStorage.getItem('lastSubscriptionVerified')
      
      console.log('💾 localStorage values:', {
        storedSubscription,
        storedTrial,
        storedTrialEnd,
        lastVerified
      });
      
      // Only trust localStorage if verified recently (within 5 minutes) to prevent manipulation
      const VERIFICATION_WINDOW = 5 * 60 * 1000 // 5 minutes
      const now = Date.now()
      const lastVerifiedTime = lastVerified ? parseInt(lastVerified) : 0
      const isRecentlyVerified = (now - lastVerifiedTime) < VERIFICATION_WINDOW
      
      let canSkipDatabaseCheck = false;
      
      if (isRecentlyVerified && storedSubscription === 'true') {
        console.log('💾 Using recently verified subscription from localStorage')
        updateSubscription(true)
        canSkipDatabaseCheck = true;
      }
      
      // Check stored trial with expiration and recent verification
      if (isRecentlyVerified && storedTrial === 'true' && storedTrialEnd) {
        const trialEnd = new Date(storedTrialEnd)
        const currentTime = new Date()
        
        if (trialEnd > currentTime) {
          console.log('💾 Using recently verified trial from localStorage, valid until:', trialEnd)
          updateTrialStatus(true, storedTrialEnd)
          canSkipDatabaseCheck = true;
        } else {
          console.log('⏱️ Trial in localStorage has expired, will verify with database')
          updateTrialStatus(false, null)
        }
      }
      
      // SECURITY: Periodically verify with database even with localStorage cache
      if (canSkipDatabaseCheck) {
        console.log('✅ Using cached status but will verify in background');
        setSubscriptionChecking(false)
        setLoading(false)
        
        // Skip background verification to prevent infinite loops
        return;
      }
      
      console.log('🔍 No valid cache found, querying database for subscription status');
      console.log('🔍 Querying for email:', email.toLowerCase());
      
      // Query for both subscription and trial status with timeout
      let data, error;
      try {
        console.log('🔄 Starting database query...');
        const result = await Promise.race([
          supabase
            .from('profiles')
            .select('is_subscribed, is_trial, trial_end_date')
            .eq('email', email.toLowerCase())
            .single(),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Database query timeout after 10 seconds')), 10000)
          )
        ]);
        data = result.data;
        error = result.error;
        console.log('📊 Database query completed:', { data, error });
      } catch (timeoutError) {
        console.error('❌ Database query failed or timed out:', timeoutError);
        error = timeoutError;
        data = null;
      }
        
      if (error) {
        console.error('❌ Fetch subscription/trial error:', error)
        // CRITICAL: Always set loading to false on error to prevent infinite loading
        console.log('⚠️ Setting loading to false due to database error')
        console.log('⚠️ Falling back to cached subscription status')
        setSubscriptionChecking(false)
        setLoading(false)
        return
      } 
      
      if (data) {
        console.log('✅ Account status from DB:', data)
        
        // SECURITY: Update verification timestamp when we get fresh data from database
        localStorage.setItem('lastSubscriptionVerified', Date.now().toString())
        
        // If they're subscribed in DB, update immediately
        if (data.is_subscribed) {
          // 🎯 Check if this is a NEW subscription (not in localStorage)
          const wasSubscribed = localStorage.getItem('hasSubscription') === 'true';
          const isNewSubscription = !wasSubscribed;
          
          console.log('✅ Updating subscription status to true immediately')
          updateSubscription(true)
          
          // Track conversion for new subscriptions
          trackConversionIfNew(email, isNewSubscription);
          
          setSubscriptionChecking(false)
          setLoading(false)
          return
        }
        
        // ENHANCED: Check trial with expiration handling
        if (data.is_trial && data.trial_end_date) {
          const trialEnd = new Date(data.trial_end_date)
          const now = new Date()
          
          if (trialEnd > now) {
            // Active trial
            console.log('🎁 Active trial found, valid until:', trialEnd)
            updateTrialStatus(true, data.trial_end_date)
            setSubscriptionChecking(false)
            setLoading(false)
            return
          } else {
            // FIXED: Trial expired - update both local and database
            console.log('⏱️ Trial has expired, updating database and local state')
            updateTrialStatus(false, null)
            // Update database to reflect expiration with explicit null endDate
            await updateTrialInDatabase(email, false, null)
          }
        } else {
          // No trial found
          updateTrialStatus(false, null)
        }
        
        // If no subscription and no valid trial, check if we need to start polling
        const hasActiveSubscription = data.is_subscribed
        const hasActiveTrial = data.is_trial && data.trial_end_date && new Date(data.trial_end_date) > new Date()
        
        console.log('🔍 Subscription status check:', {
          hasActiveSubscription,
          hasActiveTrial,
          is_subscribed: data.is_subscribed,
          is_trial: data.is_trial,
          trial_end_date: data.trial_end_date
        });
        
        if (!hasActiveSubscription && !hasActiveTrial) {
          console.log('🔄 No valid subscription or trial found, starting polling...')
          
          // Clear any existing polling first
          if (pollingCleanupRef.current) {
            pollingCleanupRef.current()
          }
          
          // Start new polling and store cleanup function
          pollingCleanupRef.current = startPolling(email)
        } else {
          console.log('✅ Valid subscription or trial found, setting loading to false')
          setSubscriptionChecking(false)
          setLoading(false)
        }
      } else {
        // No data returned from query
        console.log('⚠️ No profile data found for email:', email)
        setSubscriptionChecking(false)
        setLoading(false)
      }
    } catch (err) {
      console.error('❌ fetchSubscription error:', err)
      setSubscriptionChecking(false)
      setLoading(false)
    } finally {
      // Reset the flag to allow future fetches
      fetchingSubscriptionRef.current = false
      
      // Log the final state for debugging
      console.log('Auth flow status check completed:', {
        subscription: hasSubscription,
        trial: isTrial,
        trialEnd: trialEndDate,
        loading: false,
        subscriptionChecking: false
      })
    }
  }

  const logout = async () => {
    try {
      console.log('🚪 Logging out...')
      await supabase.auth.signOut()
      clearSession()
      // Redirect to home after logout
      navigate('/')
    } catch (err) {
      console.error('❌ Logout error:', err)
      // Still clear local session even if supabase signout fails
      clearSession()
      navigate('/')
    }
  }

  const clearSession = () => {
    // Clear any active polling
    if (pollingCleanupRef.current) {
      pollingCleanupRef.current()
      pollingCleanupRef.current = null
    }
    
    setSession(null)
    setUser(null)
    updateSubscription(false)
    updateTrialStatus(false, null)
    setSubscriptionChecking(false)
    setLoading(false)
    // Clear all auth-related localStorage items
    localStorage.removeItem('hasSubscription')
    localStorage.removeItem('isTrial')
    localStorage.removeItem('trialEndDate')
  }

  const value = {
    user,
    session,
    hasSubscription,
    isTrial,
    trialEndDate,
    trialDaysLeft,
    loading,
    logout,
    clearSession,
    updateSubscription,
    setUser,
    setSession,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}